import React from "react";
import classNames from "classnames";

import Conditional from "../../../common/conditional";
import CustomSection from "../../custom-general/custom-section";
import ParseHTML from "../../../common/ParseHTML";
import PDFTablePage from "../../../themed/pdf/pdfTablePage";
import PAUTConstraint from "../paut-constraint";

import { Column } from "../../../../utils/models";
import { PAUTScanDataItem } from "../pautScanDataItems";
import { getResultItemStyle } from "../../../../utils/data";
import { getTotalScanLength } from "../../../../utils/utilities";

import styles from './style.module.scss';

interface Props {
    pautScanItems: PAUTScanDataItem[];
}

const CustomPAUTData: React.FC<Props> = ({ pautScanItems }) => {
    return ( 
        <>
            {pautScanItems.map((pautItem) => (
                <CustomSection key={pautItem.weldNumber} sectionTitle="Result" sectionSubtitle="Scan Data">
                    <div className={classNames(["p-1", styles.summary])}>
                        <div className="container-fluid overflow-hidden">
                            <div className={classNames(["row", styles.summaryTitleContainer])}>
                                <div className={classNames(["col", styles.summaryTitle])}>
                                    <p>
                                        Weld Number
                                    </p>
                                </div>
                                <div className={classNames(["col p-1", styles.summaryTitle, styles.summaryTitleTender])}>
                                    <p>
                                        {pautItem.weldNumber}
                                    </p>
                                </div>
                                <div className={classNames(["col p-1", styles.summaryTitle])}>
                                    <p>
                                        Scan Number
                                    </p>
                                </div>
                                <div className={classNames(["col p-1", styles.summaryTitle, styles.summaryTitleTender])}>
                                    <p>
                                        {pautItem.scanNumber}
                                    </p>
                                </div>
                                <div className={classNames(["col p-1", styles.summaryTitle])}>
                                    <p>
                                        Minimum Measured Thickness (mm)
                                    </p>
                                </div>
                                <div className={classNames(["col p-1", styles.summaryTitle, styles.summaryTitleTender, "border-0"])}>
                                    <p>
                                        {pautItem.minimumMeasuredThickness}
                                    </p>
                                </div>
                            </div>
                           
                            <div className={classNames(["row mt-1", styles.summaryTitleContainer])}>
                                <div className={classNames(["col", styles.summaryTitle])}>
                                    <p>
                                        Scan Start (mm)
                                    </p>
                                </div>
                                <div className={classNames(["col p-1", styles.summaryTitle, styles.summaryTitleTender])}>
                                    <p>
                                        {pautItem.scanStart}
                                    </p>
                                </div>
                                <div className={classNames(["col p-1", styles.summaryTitle])}>
                                    <p>
                                        Scan End (mm)
                                    </p>
                                </div>
                                <div className={classNames(["col p-1", styles.summaryTitle, styles.summaryTitleTender])}>
                                    <p>
                                        {pautItem.scanEnd}
                                    </p>
                                </div>
                                <div className={classNames(["col p-1", styles.summaryTitle])}>
                                    <p>
                                        Total Scan Length (mm)
                                    </p>
                                </div>
                                <div className={classNames(["col p-1", styles.summaryTitle, styles.summaryTitleTender, "border-0"])}>
                                    <p>
                                        {getTotalScanLength(pautItem.scanStart, pautItem.scanEnd)}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <Conditional isVisible={pautItem.aScan.length === 0 && pautItem.sScan.length === 0 && pautItem.cScan.length === 0}>
                            <PAUTConstraint />
                        </Conditional>

                        {pautItem.aScan.length > 0 && (
                            <div className={classNames(["mt-1", styles.summaryScan])}>
                                <div className={classNames(["p-1", styles.summaryScanHeader])}>
                                    <p>A-SCAN</p>
                                </div>
                                <div className={styles.summaryScanBody}>
                                    <img src={pautItem.aScan[0].src} alt={pautItem.aScan[0].caption} />
                                </div>
                            </div>
                        )}
                        
                        {pautItem.sScan.length > 0 && (
                            <div className={classNames(["mt-1", styles.summaryScan])}>
                                <div className={classNames(["p-1", styles.summaryScanHeader])}>
                                    <p>S-SCAN</p>
                                </div>
                                <div className={styles.summaryScanBody}>
                                    <img src={pautItem.sScan[0].src} alt={pautItem.sScan[0].caption} />
                                </div>
                            </div>
                        )}
                        
                        {pautItem.cScan.length > 0 && (
                            <div className={classNames(["mt-1", styles.summaryScan])}>
                                <div className={classNames(["p-1", styles.summaryScanHeader])}>
                                    <p>C-SCAN</p>
                                </div>
                                <div className={styles.summaryScanBody}>
                                    <img src={pautItem.cScan[0].src} alt={pautItem.cScan[0].caption} />
                                </div>
                            </div>
                        )}

                        <div className="mt-1">
                            <PDFTablePage 
                                data={[pautItem]} 
                                columns={COLUMNS} 
                            />
                        </div>

                        <div className="container-fluid mt-1">
                            <div className="row gap-1">
                                <div className={classNames(["col", styles.summaryContainer])}>
                                    <p className={styles.summaryContainerHeader}>
                                        LIMITATION (IF ANY):
                                    </p>
                                    <p>
                                        <ParseHTML text={pautItem.limitation} />
                                    </p>
                                </div>
                                <div className={classNames(["col", styles.summaryContainer])}>
                                    <p className={styles.summaryContainerHeader}>
                                        REMARK (IF ANY):
                                    </p>
                                    <p>
                                        <ParseHTML text={pautItem.remark} />
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </CustomSection>
            ))}
        </>
     );
};

const WARNING_STYLE = { 
    backgroundColor: '#ffff0f',
    color: '#000000'
};


const COLUMNS: Column[] = [
    {
        path: 'thickness',
        label: 'THICKNESS (mm)',
        style: () => WARNING_STYLE
    },
    {
        path: 'pipeDia',
        label: 'PIPE DIA (inch)',
        style: () => WARNING_STYLE
    },
    {
        path: 'defectDepth',
        label: 'DEFECT DEPTH (mm)',
        style: () => WARNING_STYLE,
    },
    {
        path: 'defectLength',
        label: 'DEFECT LENGTH (mm)',
        style: () => WARNING_STYLE,
    },
    {
        path: 'defectHeight',
        label: 'DEFECT HEIGHT (mm)',
        style: () => WARNING_STYLE,
    },
    {
        path: 'defectType',
        label: 'DEFECT TYPE',
    },
    {
        path: 'result',
        label: 'RESULT',
        style: (item) => getResultItemStyle(item),
    },
];
 
export default CustomPAUTData;